import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { Objective } from 'src/app/models/schema';
import { FirestoreService } from 'src/app/services/firestore/firestore';
import {
  LoadObjectives,
  LoadObjectivesSuccess,
  LoadObjectivesFailure,
  AddObjectivesBatch,
  AddObjectivesBatchSuccess,
  AddObjectivesBatchFailure,
  UpdateObjective,
  UpdateObjectiveSuccess,
  UpdateObjectiveFailure,
  DeleteObjective,
  DeleteObjectiveSuccess,
  DeleteObjectiveFailure,
  DeleteObjectivesByGoal,
  DeleteObjectivesByGoalSuccess,
  DeleteObjectivesByGoalFailure,
} from './objectives.actions';
import { ObjectivesState } from './objectives.state';

/**
 * Objectives Service
 * 
 * Service layer for managing objectives with Firestore integration.
 * Objectives are stored in: partners/{partnerId}/objectives/{objectiveId}
 */
@Injectable({
  providedIn: 'root',
})
export class ObjectivesService {
  objectives$: Observable<Objective[]>;

  constructor(
    private store: Store,
    private firestoreService: FirestoreService
  ) {
    this.objectives$ = this.store.select(ObjectivesState.objectives);
  }

  /**
   * Get objectives array (synchronous)
   */
  get objectives(): Objective[] {
    return this.store.selectSnapshot(ObjectivesState.objectives);
  }

  /**
   * Load objectives for a partner
   * Uses lastSync to only fetch updated objectives
   */
  async loadObjectives(partnerId: string): Promise<void> {
    try {
      await this.store.dispatch(new LoadObjectives(partnerId)).toPromise();

      const collectionPath = `partners/${partnerId}/objectives`;
      const lastSync = this.store.selectSnapshot(ObjectivesState.lastSync);

      // Build query options
      const queryOptions: any = {
        orderBy: [{ field: 'targetDate', direction: 'asc' }],
      };

      // If we have a lastSync, only get objectives updated since then
      if (lastSync) {
        queryOptions.where = [
          { field: 'updatedAt', operator: '>', value: lastSync },
        ];
      }

      const objectives = await this.firestoreService.getCollectionAsync<Objective>(
        collectionPath,
        queryOptions
      );

      await this.store.dispatch(new LoadObjectivesSuccess(objectives)).toPromise();
    } catch (error: any) {
      console.error('[ObjectivesService] Error loading objectives:', error);
      await this.store.dispatch(new LoadObjectivesFailure(error.message || 'Failed to load objectives')).toPromise();
      throw error;
    }
  }

  /**
   * Add multiple objectives (used during goal creation)
   */
  async addObjectivesBatch(objectives: Objective[], partnerId: string): Promise<void> {
    try {
      await this.store.dispatch(new AddObjectivesBatch(objectives, partnerId)).toPromise();

      const collectionPath = `partners/${partnerId}/objectives`;
      
      // Use Firestore batch write for efficiency
      const batch = this.firestoreService.createBatch();
      
      objectives.forEach((obj) => {
        const docRef = this.firestoreService.getDocumentReference(`${collectionPath}/${obj.id}`);
        batch.set(docRef, obj);
      });

      await this.firestoreService.commitBatchAsync(batch);

      await this.store.dispatch(new AddObjectivesBatchSuccess(objectives)).toPromise();
    } catch (error: any) {
      console.error('[ObjectivesService] Error adding objectives batch:', error);
      await this.store.dispatch(new AddObjectivesBatchFailure(error.message || 'Failed to add objectives')).toPromise();
      throw error;
    }
  }

  /**
   * Update a single objective
   */
  async updateObjective(objective: Objective, partnerId: string): Promise<void> {
    try {
      await this.store.dispatch(new UpdateObjective(objective, partnerId)).toPromise();

      const docPath = `partners/${partnerId}/objectives/${objective.id}`;
      const updateData = {
        ...objective,
        updatedAt: Date.now(),
      };

      await this.firestoreService.updateDocumentAsync(docPath, updateData);

      await this.store.dispatch(new UpdateObjectiveSuccess(updateData as Objective)).toPromise();
    } catch (error: any) {
      console.error('[ObjectivesService] Error updating objective:', error);
      await this.store.dispatch(new UpdateObjectiveFailure(error.message || 'Failed to update objective')).toPromise();
      throw error;
    }
  }

  /**
   * Delete a single objective
   */
  async deleteObjective(objectiveId: string, partnerId: string): Promise<void> {
    try {
      await this.store.dispatch(new DeleteObjective(objectiveId, partnerId)).toPromise();

      const docPath = `partners/${partnerId}/objectives/${objectiveId}`;
      await this.firestoreService.deleteDocumentAsync(docPath);

      await this.store.dispatch(new DeleteObjectiveSuccess(objectiveId)).toPromise();
    } catch (error: any) {
      console.error('[ObjectivesService] Error deleting objective:', error);
      await this.store.dispatch(new DeleteObjectiveFailure(error.message || 'Failed to delete objective')).toPromise();
      throw error;
    }
  }

  /**
   * Delete all objectives for a goal (cascade delete)
   */
  async deleteObjectivesByGoal(goalId: string, partnerId: string): Promise<void> {
    try {
      await this.store.dispatch(new DeleteObjectivesByGoal(goalId, partnerId)).toPromise();

      const collectionPath = `partners/${partnerId}/objectives`;
      
      // Find all objectives for this goal
      const objectives = await this.firestoreService.getCollectionAsync<Objective>(
        collectionPath,
        { where: [{ field: 'goalId', operator: '==', value: goalId }] }
      );

      // Delete all in batch
      if (objectives.length > 0) {
        const batch = this.firestoreService.createBatch();
        
        objectives.forEach((obj) => {
          const docRef = this.firestoreService.getDocumentReference(`${collectionPath}/${obj.id}`);
          batch.delete(docRef);
        });

        await this.firestoreService.commitBatchAsync(batch);
      }

      await this.store.dispatch(new DeleteObjectivesByGoalSuccess(goalId)).toPromise();
    } catch (error: any) {
      console.error('[ObjectivesService] Error deleting objectives by goal:', error);
      await this.store.dispatch(new DeleteObjectivesByGoalFailure(error.message || 'Failed to delete objectives')).toPromise();
      throw error;
    }
  }

  /**
   * Get objectives for a specific goal
   */
  getObjectivesByGoal(goalId: string): Objective[] {
    const selector = ObjectivesState.objectivesByGoal;
    return this.store.selectSnapshot(selector)(goalId);
  }

  /**
   * Get objectives for a specific student
   */
  getObjectivesByStudent(studentId: string): Objective[] {
    const selector = ObjectivesState.objectivesByStudent;
    return this.store.selectSnapshot(selector)(studentId);
  }

  /**
   * Get objectives for a specific skill
   */
  getObjectivesBySkill(skillId: string): Objective[] {
    const selector = ObjectivesState.objectivesBySkill;
    return this.store.selectSnapshot(selector)(skillId);
  }

  /**
   * Get upcoming milestones within specified days
   */
  getUpcomingMilestones(days: number = 30): Objective[] {
    const selector = ObjectivesState.upcomingMilestones;
    return this.store.selectSnapshot(selector)(days);
  }
}
